'use client'
import { useState, useEffect, useRef, Suspense, useMemo } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/navigation';
import { getCurrentMemberLogin } from '@/services/memberLoginApi';
import VisitorCounter from '@/components/VisitorCounter';
import { encryptBookingParams, decryptBookingParams } from '@/utils/encryption';
import { fetchRoomAvailability, fetchHotelDetails } from '@/services/api';
import { getPropertyCode, savePropertyCode, getStoredPropertyCode, ensureRequiredParams, validatePropertyCode } from '@/utils/propertyManager';
import ErrorBoundary from '@/components/ErrorBoundary';
import ReactMarkdown from 'react-markdown';
// Import debug utilities for development
if (process.env.NODE_ENV === 'development') {
  import('@/utils/faviconDebug');
  import('@/utils/faviconTestUtility');
}
import { 
  initializeSessionMonitoring, 
  handleSessionExpiry, 
  getSavedProperty, 
  saveProperty, 
  updateLastActivity,
  isSessionExpired 
} from '@/utils/sessionManager';
import NoPropertyAvailable from '@/components/NoPropertyAvailable';
import dynamic from 'next/dynamic';
import LoadingUI from '@/components/LoadingUI';
import { LanguageProvider } from '@/contexts/LanguageContext';
import SearchForm from '@/components/SearchForm';
import { CartProvider, useCart } from '@/contexts/CartContext'; // Added CartProvider and useCart
import FloatingCart from '@/components/FloatingCart';
import { Analytics, useAnalytics } from '@/components/Analytics';
import MemberLoginModal from '@/components/MemberLoginModal';
import { useMembership } from '@/contexts/MembershipContext';
import SessionWarning from '@/components/SessionWarning';
import PromoBannerSection from '@/components/PromoBannerSection';
import { RoomSkeletonList } from '@/components/RoomSkeleton';

// Format date as YYYY-MM-DD
const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Banner Carousel Component
const BannerCarousel = ({ bannerImages }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Filter out null or undefined banner images
  const validBannerImages = bannerImages.filter(img => img !== null && img !== undefined && img !== "");
  
  if (validBannerImages.length === 0) {
    return null;
  }
  
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % validBannerImages.length);
  };
  
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? validBannerImages.length - 1 : prevIndex - 1
    );
  };
  
  // Auto-slide every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 5000);
    
    return () => clearInterval(timer);
  }, []);
  
  return (
    <div className="relative w-full h-[200px] sm:h-[300px] md:h-[400px] overflow-hidden rounded-lg mb-4 sm:mb-6">
      <Image 
        src={validBannerImages[currentIndex] || null} 
        alt="Hotel Banner"
        fill
        className="object-cover transition-opacity duration-500"
        priority
      />
      
      {validBannerImages.length > 1 && (
        <>
          <button 
            onClick={prevSlide} 
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 sm:p-2 rounded-full"
          >
            <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button 
            onClick={nextSlide} 
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 sm:p-2 rounded-full"
          >
            <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex space-x-1.5 sm:space-x-2">
            {validBannerImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${
                  index === currentIndex ? "bg-white w-3 sm:w-4" : "bg-white/50"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Hotel Info Header Component
const HotelInfoHeader = ({ hotelInfo }) => {
  const primaryColor = hotelInfo?.primarycolor || "#820c04";
  const hotelName = hotelInfo?.NAME || "";
  const hotelAddress = hotelInfo?.address || "";
  const hotelPhone = hotelInfo?.phone || hotelInfo?.telephone || "";
  const hotelWhatsapp = hotelInfo?.whatsapp || hotelInfo?.wa || "";

  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* Solid top accent bar */}
      <div className="h-1 w-full" style={{ backgroundColor: primaryColor }} />

      <div className="px-5 md:px-8 py-4 md:py-5">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            {/* Elegant badge */}
            <div className="mb-2">
              <span
                className="inline-block px-2.5 py-0.5 text-[10px] md:text-xs font-semibold tracking-wider uppercase text-white rounded-full"
                style={{ backgroundColor: primaryColor }}
              >
                Official Website
              </span>
            </div>

            {/* Hotel name */}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 mb-2 leading-tight">
              {hotelName}
            </h1>

            {/* Contact info row */}
            <div className="flex flex-wrap items-center gap-2 md:gap-2.5 text-sm text-gray-600">
              {hotelAddress && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
                  <svg className="w-4 h-4 shrink-0" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-light">{hotelAddress}</span>
                </div>
              )}
              {hotelPhone && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
                  <svg className="w-4 h-4 shrink-0" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="font-light">{hotelPhone}</span>
                </div>
              )}
              {hotelWhatsapp && (
                <a
                  href={`https://wa.me/${hotelWhatsapp.replace(/\D/g,'')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50 border border-green-200 hover:bg-green-100 transition-colors"
                >
                  <svg className="w-4 h-4 text-green-600 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <span className="font-light text-green-700">{hotelWhatsapp}</span>
                </a>
              )}
            </div>

            {/* Check-in / Check-out */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
                <svg className="w-4 h-4" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span className="text-sm font-medium">Check-in 14:00</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
                <svg className="w-4 h-4" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="text-sm font-medium">Check-out 12:00</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col gap-3 w-full lg:w-auto shrink-0">
            <a
              href="#room-selection"
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-bold rounded-xl shadow-md text-white transition-all duration-200 hover:scale-105 hover:shadow-lg"
              style={{ backgroundColor: primaryColor }}
              onClick={(e) => {
                e.preventDefault();
                const roomSelection = document.getElementById('room-selection');
                if (roomSelection) roomSelection.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Lihat Kamar
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Promotion Notice Component - Displays promotion_banner / promotion_banner2 (text/string) below the banner
const PromotionNotice = ({ hotelInfo }) => {
  const primaryColor = hotelInfo?.primarycolor || "#820c04";

  const notices = [
    { text: hotelInfo?.promotion_banner, icon: 'promo' },
    { text: hotelInfo?.promotion_banner2, icon: 'info' }
  ].filter(n => typeof n.text === 'string' && n.text.trim() !== '');

  if (notices.length === 0) {
    return null;
  }

  // Build a subtle tinted gradient using the hotel primary color
  const isHexColor = /^#[0-9a-fA-F]{6}$/.test(primaryColor);
  const gradientStyle = isHexColor
    ? { background: `linear-gradient(135deg, ${primaryColor}1a 0%, ${primaryColor}0d 55%, rgba(255,255,255,0.6) 100%)` }
    : { background: 'linear-gradient(135deg, rgba(130,12,4,0.10) 0%, rgba(130,12,4,0.05) 55%, rgba(255,255,255,0.6) 100%)' };

  return (
    <div className="relative z-10 mt-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
      {notices.map((notice, index) => (
        <div
          key={index}
          className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 border-l-4 shadow-sm overflow-hidden"
          style={{ ...gradientStyle, borderLeftColor: primaryColor }}
        >
          {notice.icon === 'promo' ? (
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          <div className="flex-1 min-w-0">
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <p className="text-gray-800 text-sm leading-relaxed break-words">{children}</p>
                ),
                strong: ({ children }) => (
                  <strong className="font-bold" style={{ color: primaryColor }}>{children}</strong>
                ),
                em: ({ children }) => (
                  <em className="italic">{children}</em>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc ml-4 text-gray-800 text-sm leading-relaxed break-words space-y-1">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal ml-4 text-gray-800 text-sm leading-relaxed break-words space-y-1">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="leading-relaxed">{children}</li>
                ),
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: primaryColor }}>{children}</a>
                )
              }}
            >
              {notice.text}
            </ReactMarkdown>
          </div>
        </div>
      ))}
    </div>
  );
};

// Hotel Gallery Grid Component
const HotelGallery = ({ bannerImages, hotelInfo }) => {
  // Filter out null or undefined banner images
  const validBannerImages = bannerImages.filter(img => img !== null && img !== undefined && img !== "");
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Debug: Log hotelInfo to check banner_info
  console.log("HotelGallery - hotelInfo:", hotelInfo);
  console.log("HotelGallery - banner_info1:", hotelInfo?.banner_info1);
  console.log("HotelGallery - banner_info2:", hotelInfo?.banner_info2);
  
  if (validBannerImages.length === 0) {
    // Show fallback UI instead of returning null
    return (
      <div className="mb-8">
        <HotelInfoHeader hotelInfo={hotelInfo} />

        {/* Promotion Notice - Text announcement below the hotel info header */}
        <PromotionNotice hotelInfo={hotelInfo} />

        {/* Banner Info Section - Display banner_info1 and banner_info2 if available */}
        {(hotelInfo?.banner_info1 || hotelInfo?.banner_info2) && (
          <div className="mb-6 space-y-3">
            {hotelInfo?.banner_info1 && typeof hotelInfo.banner_info1 === 'string' && hotelInfo.banner_info1.trim() !== '' && (
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-500 p-4 rounded-r-lg shadow-sm">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-800 text-sm leading-relaxed">{hotelInfo.banner_info1}</p>
                </div>
              </div>
            )}
            {hotelInfo?.banner_info2 && typeof hotelInfo.banner_info2 === 'string' && hotelInfo.banner_info2.trim() !== '' && (
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 border-l-4 border-amber-500 p-4 rounded-r-lg shadow-sm">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-gray-800 text-sm leading-relaxed">{hotelInfo.banner_info2}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fallback banner placeholder */}
        <div className="bg-gray-100 h-[400px] rounded-xl shadow-md flex items-center justify-center">
          <div className="text-center p-6">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-500 mb-2">Hotel images are loading or unavailable</p>
            <p className="text-sm text-gray-400">Please check back later for hotel photos</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mt-8 border-b border-gray-200">
          <div className="flex items-center space-x-12">
            <a 
              href="#room-selection" 
              className="flex items-center gap-2 py-4 px-2 border-b-2 text-bold"
              style={{ borderColor: hotelInfo?.primarycolor || "#820c04", color: hotelInfo?.primarycolor || "#820c04" }}
              onClick={(e) => {
                e.preventDefault();
                const roomSelection = document.getElementById('room-selection');
                if (roomSelection) {
                  roomSelection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Get hotel information from props without fallbacks
  const primaryColor = hotelInfo?.primarycolor || "#820c04";

  // Display main image larger and others as thumbnails
  return (
    <div className="mb-4">
      <HotelInfoHeader hotelInfo={hotelInfo} />

      {/* Promotion Notice - Text announcement below the hotel info header */}
      <PromotionNotice hotelInfo={hotelInfo} />

      {/* Banner Info Section - Display banner_info1 and banner_info2 if available */}
      {(hotelInfo?.banner_info1 || hotelInfo?.banner_info2) && (
        <div className="mb-6 space-y-3">
          {hotelInfo?.banner_info1 && typeof hotelInfo.banner_info1 === 'string' && hotelInfo.banner_info1.trim() !== '' && (
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-500 p-4 rounded-r-lg shadow-sm">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-gray-800 text-sm leading-relaxed">{hotelInfo.banner_info1}</p>
              </div>
            </div>
          )}
          {hotelInfo?.banner_info2 && typeof hotelInfo.banner_info2 === 'string' && hotelInfo.banner_info2.trim() !== '' && (
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 border-l-4 border-amber-500 p-4 rounded-r-lg shadow-sm">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-gray-800 text-sm leading-relaxed">{hotelInfo.banner_info2}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Photo Modal */}
      {showAllPhotos && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 overflow-auto flex">
          <div className="relative w-full h-full p-4">
            <button 
              className="absolute top-4 right-4 text-white bg-black/50 p-3 rounded-full hover:bg-black/75 transition-colors z-10"
              onClick={() => setShowAllPhotos(false)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
              {validBannerImages.map((img, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-[4/3] relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                    <Image
                      src={img}
                      alt={`Hotel photo ${index + 1}`}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main gallery layout */}
      {validBannerImages.length > 0 ? (
        <div className="relative">
          {/* Mobile Banner Carousel - Only visible on mobile */}
          <div className="md:hidden">
            <div className="relative h-[180px] sm:h-[220px] rounded-xl overflow-hidden shadow-lg">
              {validBannerImages.map((img, index) => (
                <div 
                  key={index} 
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    currentImageIndex === index ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <Image
                    src={img}
                    alt={`Hotel photo ${index + 1}`}
                    fill
                    className="object-cover"
                    priority={index === 0}
                  />
                </div>
              ))}
              
              {/* Navigation arrows */}
              {validBannerImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex((prev) => 
                        prev === 0 ? validBannerImages.length - 1 : prev - 1
                      );
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/60 transition-colors backdrop-blur-sm"
                    aria-label="Previous image"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex((prev) => 
                        prev === validBannerImages.length - 1 ? 0 : prev + 1
                      );
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/60 transition-colors backdrop-blur-sm"
                    aria-label="Next image"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  
                  {/* Dots indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-1.5">
                    {validBannerImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentImageIndex ? "bg-white w-5" : "bg-white/50"
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Desktop Grid Layout - Hidden on mobile */}
          <div className="hidden md:block">
            <div className="grid grid-cols-12 gap-2 h-[280px] lg:h-[360px]">
              {/* Main large image - smaller now */}
              <div className="col-span-5 row-span-2 relative rounded-xl overflow-hidden shadow-lg group">
                {validBannerImages[0] && (
                  <div className="relative h-full w-full">
                    <Image
                      src={validBannerImages[0]}
                      alt="Hotel photo 1"
                      fill
                      className="object-cover transition-transform duration-700 group-hover:scale-105"
                      priority
                    />
                  </div>
                )}
              </div>

              {/* Right side image grid - takes 7 columns now */}
              <div className="col-span-7 grid grid-rows-2 gap-2">
                {/* Top row - 2 images */}
                <div className="grid grid-cols-2 gap-2 h-[136px] lg:h-[176px]">
                  {/* Top right first image */}
                  {validBannerImages.length > 1 && (
                    <div className="relative rounded-xl overflow-hidden shadow-lg group h-full">
                      <Image
                        src={validBannerImages[1]}
                        alt="Hotel photo 2"
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                  
                  {/* Top right second image */}
                  {validBannerImages.length > 2 && (
                    <div className="relative rounded-xl overflow-hidden shadow-lg group h-full">
                      <Image
                        src={validBannerImages[2]}
                        alt="Hotel photo 3"
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                </div>

                {/* Bottom row - 2 images */}
                <div className="grid grid-cols-2 gap-2 h-[136px] lg:h-[176px]">
                  {/* Bottom right first image */}
                  {validBannerImages.length > 3 && (
                    <div className="relative rounded-xl overflow-hidden shadow-lg group h-full">
                      <Image
                        src={validBannerImages[3]}
                        alt="Hotel photo 4"
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                  
                  {/* Bottom right second image - with overlay if more images exist */}
                  {validBannerImages.length > 4 && (
                    <div className="relative rounded-xl overflow-hidden shadow-lg h-full">
                      <Image
                        src={validBannerImages[4]}
                        alt="Hotel photo 5"
                        fill
                        className="object-cover"
                      />
                      <div 
                        className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer hover:bg-black/60 transition-colors"
                        onClick={() => setShowAllPhotos(true)}
                      >
                        {validBannerImages.length > 5 ? (
                          <div className="text-center text-white">
                            <p className="text-xl font-bold">+{validBannerImages.length - 5}</p>
                            <p className="text-sm">More Photos</p>
                          </div>
                        ) : (
                          <div className="text-white font-medium">View All</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Show all photos button - Visible on all screen sizes */}
          <button 
            className="absolute bottom-4 right-4 bg-white/95 text-gray-800 flex items-center gap-2 py-2.5 px-4 rounded-xl shadow-lg hover:bg-white transition-all duration-200 backdrop-blur-sm hover:scale-105"
            onClick={() => setShowAllPhotos(true)}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 5.5C4 4.67157 4.67157 4 5.5 4H18.5C19.3284 4 20 4.67157 20 5.5V18.5C20 19.3284 19.3284 20 18.5 20H5.5C4.67157 20 4 19.3284 4 18.5V5.5Z" stroke="currentColor" strokeWidth="1.5" />
              <path d="M4 16.5L8 12.5L10 14.5L15 9.5L20 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            Lihat Semua Foto
          </button>
        </div>
      ) : (
        <div className="bg-gray-100 h-[400px] rounded-lg shadow-md flex items-center justify-center">
          <p className="text-gray-500">No images available</p>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="mt-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <a 
            href="#room-selection" 
            className="inline-flex items-center gap-2.5 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full text-white font-semibold text-sm sm:text-base shadow-md hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            style={{ backgroundColor: primaryColor }}
            onClick={(e) => {
              e.preventDefault();
              const roomSelection = document.getElementById('room-selection');
              if (roomSelection) {
                roomSelection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span>Book a Room</span>
            <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </a>
          <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-gray-500">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Scroll down to see our available rooms & rates
          </span>
        </div>
      </div>
    </div>
  );
};

// Room Card Component
const RoomCard = ({ room, checkIn, checkOut, onBookNow, nights: propNights }) => {
  const { room_info, availability, rate_details, available_rates } = room;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { addToCart } = useCart(); // Use cart context
  const { trackClick } = useAnalytics(); // Add analytics hook
  
  // Filter out inactive rates (is_active: 0 = inactive, 1 = active)
  const activeRates = available_rates?.filter(rate => rate.is_active !== 0) || [];
  
  // Initialize selectedRate properly
  const [selectedRate, setSelectedRate] = useState(() => {
    if (rate_details && rate_details.final_rate > 0) {
      return rate_details;
    }
    else if (activeRates && activeRates.length > 0) {
      return activeRates[0];
    }
    return { final_rate: 0, include_breakfast: false, benefits: [] };
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showAllFacilities, setShowAllFacilities] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#059669");
  const [isAddedToCart, setIsAddedToCart] = useState(false); // Track if room is added to cart
  
  // Check if we're filtering by roomtype (URL has roomtype parameter)
  const [isFilteredByRoomtype, setIsFilteredByRoomtype] = useState(false);
  
  useEffect(() => {
    // Get primary color from CSS variable
    const color = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim();
    if (color) {
      setPrimaryColor(color);
    }
    
    // Check if URL has roomtype parameter
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      setIsFilteredByRoomtype(!!urlParams.get('roomtype'));
    }
  }, []);

  // Reset added to cart state after a short delay
  useEffect(() => {
    let timer;
    if (isAddedToCart) {
      timer = setTimeout(() => {
        setIsAddedToCart(false);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [isAddedToCart]);
  
  // Use nights from props if available, otherwise calculate
  const nights = propNights || Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)) || 1;
  
  // Calculate total amount based on selected rate and nights
  const totalAmount = selectedRate.final_rate * nights;

  // Handle adding room to cart - accept rate parameter to avoid state delay
  const handleAddToCart = (rateToUse = null) => {
    // Use provided rate or fall back to selectedRate
    const currentRate = rateToUse || selectedRate;
    
    // Get check-in and check-out dates from URL parameters
    let urlCheckIn = checkIn;
    let urlCheckOut = checkOut;
    
    // Ensure we have the latest URL parameters
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      urlCheckIn = urlParams.get('checkIn') || checkIn;
      urlCheckOut = urlParams.get('checkOut') || checkOut;
    }
    
    // Get rooms_to_sell from availability (same for all rate plans)
    // CRITICAL: This prevents overbooking by limiting cart quantity
    const roomsAvailable = 
      availability?.rooms_to_sell || 
      availability?.rooms_available || 
      room.availability?.rooms_to_sell || 
      room.availability?.rooms_available || 
      999;
    
    // Validate that we have a valid number
    const validatedRoomsAvailable = typeof roomsAvailable === 'number' && roomsAvailable > 0 
      ? roomsAvailable 
      : 999;
    
    const roomToAdd = {
      ...room,
      rate_details: currentRate,
      checkIn: urlCheckIn, 
      checkOut: urlCheckOut,
      nights: propNights || Math.ceil((new Date(urlCheckOut) - new Date(urlCheckIn)) / (1000 * 60 * 60 * 24)) || 1,
      // CRITICAL: Ensure rooms_available is included for quantity validation in cart
      room_info: {
        ...room.room_info,
        rooms_available: validatedRoomsAvailable
      }
    };
    
    console.log('=== ADD TO CART DEBUG (page.js) ===');
    console.log('Room type:', room_info?.room_type);
    console.log('Selected rate plan:', currentRate?.ratecode || 'N/A');
    console.log('availability.rooms_to_sell:', availability?.rooms_to_sell);
    console.log('roomsAvailable (raw):', roomsAvailable);
    console.log('validatedRoomsAvailable (final):', validatedRoomsAvailable);
    console.log('roomToAdd.room_info.rooms_available:', roomToAdd.room_info.rooms_available);
    console.log('===================================');
    
    addToCart(roomToAdd);
    setIsAddedToCart(true);

    // Track add to cart event
    trackClick(`add_to_cart_${room_info.room_type}`);
  };
  
  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Format crossed price (15% higher)
  const formatCrossedPrice = (price) => {
    return formatPrice(Math.round(price * 1.15));
  };

  // Find the lowest rate from active rates only
  const lowestRate = activeRates && activeRates.length > 0 
    ? activeRates.reduce((lowest, current) => 
        current.final_rate < lowest.final_rate ? current : lowest, activeRates[0])
    : rate_details && rate_details.final_rate > 0 
      ? rate_details 
      : { final_rate: 0 };

  // Function to determine if we should display rate information
  const hasValidRate = () => {
    // If we're filtering by roomtype, always return true to ensure button appears
    if (isFilteredByRoomtype) return true;
    
    return (activeRates && activeRates.length > 0) || 
           (rate_details && rate_details.final_rate > 0) ||
           (availability && availability.dates && 
            availability.dates.length > 0 && 
            availability.dates[0].final_rate > 0);
  };

  // Get facility icon
  const getFacilityIcon = (facilityName) => {
    const icons = {
      'wifi': (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
        </svg>
      ),
      'ac': (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.25 4.5l3.5 3.5m-3.5-3.5l-3.5 3.5m3.5-3.5v12m0-12l3.5 3.5m-3.5-3.5l-3.5 3.5m14-1.5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      'tv': (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      'bathroom': (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10c0 6-9 13-9 13s-9-7-9-13a9 9 0 0118 0z M12 7a3 3 0 100 6 3 3 0 000-6z" />
        </svg>
      ),
      'default': (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )
    };

    const normalizedName = facilityName?.toLowerCase();
    return icons[normalizedName] || icons.default;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 group">
      <div className="flex flex-col lg:flex-row">
        {/* Left Section: Image Gallery */}
        <div className="lg:w-2/5 relative">
          {/* Image Gallery */}
          <div className="relative h-72 lg:h-full bg-gradient-to-br from-gray-100 to-gray-200">
            {room_info.images?.length > 0 ? (
              <>
                {room_info.images[currentImageIndex] && (
                  <Image
                    src={room_info.images[currentImageIndex]}
                    alt={room_info.room_type}
                    fill
                    className="object-cover"
                  />
                )}
                {/* Image overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {room_info.images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => 
                          prev === 0 ? room_info.images.length - 1 : prev - 1
                        );
                      }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm text-gray-800 w-10 h-10 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-200 shadow-lg opacity-0 group-hover:opacity-100"
                      aria-label="Previous image"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => 
                          prev === room_info.images.length - 1 ? 0 : prev + 1
                        );
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm text-gray-800 w-10 h-10 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-200 shadow-lg opacity-0 group-hover:opacity-100"
                      aria-label="Next image"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium">
                      {currentImageIndex + 1} / {room_info.images.length}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm p-4 text-center">
                Ups, foto untuk {room_info.room_type} belum tersedia.
              </div>
            )}
          </div>
        </div>

        {/* Right Section: Content */}
        <div className="flex-1 p-6 lg:p-8">
          <div className="flex flex-col h-full">
            {/* Room Title and Meta */}
            <div className="mb-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">{room_info.room_type}</h3>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <div className="flex items-center gap-1.5 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      <span className="font-medium">{room_info.bedtype || 'Standard'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V6a2 2 0 012-2h2M4 8h16M4 8v12a2 2 0 002 2h12a2 2 0 002-2V8M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
                      </svg>
                      <span className="font-medium">{room_info.size || '-'} m²</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {room_info.description && (
                <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">{room_info.description}</p>
              )}
              
              {/* Room Facilities */}
              {room_info.facilities && room_info.facilities.length > 0 && (
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Fasilitas</h4>
                    {room_info.facilities?.length > 6 && (
                      <button
                        onClick={() => setShowAllFacilities(!showAllFacilities)}
                        className="text-xs font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                      >
                        {showAllFacilities ? (
                          <>
                            Lebih Sedikit
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </>
                        ) : (
                          <>
                            Lihat Semua
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    {(showAllFacilities ? room_info.facilities : room_info.facilities?.slice(0, 6))?.map((facility, index) => (
                      <div
                        key={facility.facility_id || index}
                        className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50/50 px-2.5 py-2 rounded-lg hover:bg-gray-100 transition-colors group relative"
                        title={facility.description}
                      >
                        <div className="p-1.5 bg-white rounded-md shadow-sm">
                          {getFacilityIcon(facility.name)}
                        </div>
                        <span className="truncate font-medium text-xs">{facility.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Check if we should display rate information */}
            {hasValidRate() ? (
              <>
                {/* Available Rates Grid (Only show if multiple rates and not filtering by roomtype) */}
                {!isFilteredByRoomtype && activeRates && activeRates.length > 1 && (
                  <div className="flex-1 mt-4 space-y-3">
                    {activeRates.map((rate) => {
                      const isLowestRate = rate.final_rate === lowestRate.final_rate;
                      const isPromo = rate.promo === 1;
                      const discount = rate.original_rate ? Math.round((1 - rate.final_rate / rate.original_rate) * 100) : 15;
                      
                      return (
                        <div
                          key={rate.ratecode || 'default-rate'}
                          className={`relative rounded-xl overflow-hidden transition-all duration-300 ${
                            isPromo
                              ? 'bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border-2 border-amber-400 shadow-lg shadow-amber-100/50 ring-2 ring-amber-200/50'
                              : isLowestRate 
                                ? 'bg-white border-2 border-emerald-500 shadow-lg shadow-emerald-100/50' 
                                : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                        >
                          {/* Promo Banner */}
                          {isPromo && (
                            <>
                              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 animate-pulse"></div>
                              <div className="absolute -right-8 top-3 rotate-45 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[10px] font-bold px-8 py-1 shadow-lg">
                                PROMO
                              </div>
                            </>
                          )}
                          
                          {/* Header Badge for Best Price */}
                          {!isPromo && isLowestRate && (
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                          )}
                          
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              {/* Left: Rate Info */}
                              <div className="flex-1 min-w-0">
                                {/* Rate Title Row */}
                                <div className="flex items-center gap-2 mb-2">
                                  {isPromo && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-rose-500 to-orange-500 text-white uppercase tracking-wide shadow-sm">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                      </svg>
                                      Special Offer
                                    </span>
                                  )}
                                  {!isPromo && isLowestRate && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wide">
                                      Best Deal
                                    </span>
                                  )}
                                  <h4 className={`font-bold text-base truncate ${
                                    isPromo ? 'text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-orange-600' : 'text-gray-900'
                                  }`}>
                                    {rate.include_breakfast ? 'With Breakfast' : 'Without Breakfast'}
                                  </h4>
                                </div>
                                
                                {/* Quick Features Row */}
                                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                  {rate.include_breakfast && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.477 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
                                      </svg>
                                      Breakfast
                                    </span>
                                  )}
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-50 text-gray-600 border border-gray-200">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                    </svg>
                                    Pay Now
                                  </span>
                                  {rate.cancellation && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
                                      Free Cancel
                                    </span>
                                  )}
                                </div>
                                
                                {/* Benefits - Compact Grid */}
                                {rate.benefits && rate.benefits.length > 0 && (
                                  <div className="grid grid-cols-1 gap-1">
                                    {rate.benefits.slice(0, 3).map((benefit, index) => (
                                      <div key={index} className="flex items-center gap-1.5 text-xs text-gray-600">
                                        <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span className="truncate">{benefit}</span>
                                      </div>
                                    ))}
                                    {rate.benefits.length > 3 && (
                                      <span className="text-[10px] text-gray-400 pl-5">+{rate.benefits.length - 3} more benefits</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Right: Price & Action */}
                              <div className="flex flex-col items-end justify-between min-w-[120px]">
                                {/* Price Section */}
                                <div className="text-right mb-3">
                                  <div className={`text-2xl font-bold ${
                                    isPromo 
                                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-orange-600' 
                                      : isLowestRate 
                                        ? 'text-emerald-600' 
                                        : 'text-gray-900'
                                  }`}>
                                    Rp {formatPrice(rate.final_rate)}
                                  </div>
                                  <div className="text-[10px] text-gray-400">per night • incl. taxes</div>
                                  {isPromo && (
                                    <div className="mt-1 text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full inline-block">
                                      🔥 Limited Offer!
                                    </div>
                                  )}
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex flex-col gap-1.5 w-full">
                                  <button
                                    onClick={() => {
                                      setSelectedRate(rate);
                                      handleAddToCart(rate);
                                    }}
                                    className={`w-full py-2 px-3 rounded-lg font-semibold text-sm text-white transition-all duration-200 ${
                                      availability.rooms_to_sell <= 0 || rate.final_rate <= 0
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : isPromo
                                          ? 'bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 shadow-md hover:shadow-lg transform hover:scale-105'
                                          : isLowestRate
                                            ? 'bg-emerald-500 hover:bg-emerald-600 shadow-md hover:shadow-lg'
                                            : 'bg-gray-800 hover:bg-gray-900'
                                    }`}
                                    disabled={availability.rooms_to_sell <= 0 || rate.final_rate <= 0}
                                  >
                                    {availability.rooms_to_sell <= 0 ? 'Sold Out' : (rate.final_rate <= 0 ? 'Unavailable' : isPromo ? '🎉 Get Promo' : 'Select')}
                                  </button>
                                  
                                  <button
                                    onClick={() => {
                                      setSelectedRate(rate);
                                      handleAddToCart(rate);
                                    }}
                                    className={`w-full py-1.5 px-3 rounded-lg font-medium text-xs transition-colors flex items-center justify-center gap-1 ${
                                      isPromo
                                        ? 'bg-white text-rose-600 border-2 border-rose-300 hover:bg-rose-50'
                                        : 'text-gray-600 border border-gray-200 hover:bg-gray-50'
                                    }`}
                                    disabled={availability.rooms_to_sell <= 0 || rate.final_rate <= 0}
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    {isPromo ? '🛒 Add Promo to Cart' : 'Add to Cart'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Single Rate Card - Always show for single rates or when filtering by roomtype */}
                {(isFilteredByRoomtype || activeRates?.length === 1 || !activeRates || activeRates.length === 0) && (() => {
                  const isPromo = selectedRate.promo === 1;
                  return (
                  <div className={`mt-4 rounded-xl overflow-hidden shadow-sm ${
                    isPromo
                      ? 'bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border-2 border-amber-400 shadow-lg shadow-amber-100/50 ring-2 ring-amber-200/50'
                      : 'bg-white border border-emerald-200'
                  }`}>
                    {/* Top accent bar */}
                    <div className={`h-1 ${
                      isPromo 
                        ? 'bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 animate-pulse' 
                        : 'bg-gradient-to-r from-emerald-500 to-teal-500'
                    }`}></div>
                    
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        {/* Left: Rate Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            {isPromo ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-rose-500 to-orange-500 text-white uppercase tracking-wide shadow-sm">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                Special Offer
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wide">
                                Best Deal
                              </span>
                            )}
                            <h4 className={`font-bold text-base ${
                              isPromo ? 'text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-orange-600' : 'text-gray-900'
                            }`}>
                              {selectedRate.include_breakfast ? 'With Breakfast' : 'Without Breakfast'}
                            </h4>
                          </div>
                          
                          {/* Quick Features */}
                          <div className="flex flex-wrap items-center gap-1.5 mb-2">
                            {selectedRate.include_breakfast && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.477 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
                                </svg>
                                Breakfast
                              </span>
                            )}
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-50 text-gray-600 border border-gray-200">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              Pay Now
                            </span>
                          </div>
                          
                          {/* Benefits */}
                          {selectedRate.benefits && selectedRate.benefits.length > 0 && (
                            <div className="grid grid-cols-1 gap-1">
                              {selectedRate.benefits.slice(0, 3).map((benefit, index) => (
                                <div key={index} className="flex items-center gap-1.5 text-xs text-gray-600">
                                  <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  <span className="truncate">{benefit}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Right: Price & Action */}
                        <div className="flex flex-col items-end justify-between min-w-[120px]">
                          <div className="text-right mb-3">
                            <div className={`text-2xl font-bold ${
                              isPromo 
                                ? 'text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-orange-600'
                                : 'text-emerald-600'
                            }`}>
                              Rp {formatPrice(selectedRate.final_rate || 352213)}
                            </div>
                            <div className="text-[10px] text-gray-400">per night • incl. taxes</div>
                            {isPromo && (
                              <div className="mt-1 text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full inline-block">
                                🔥 Limited Offer!
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-1.5 w-full">
                            <button
                              onClick={handleAddToCart}
                              className={`w-full py-2 px-3 rounded-lg font-semibold text-sm text-white transition-all duration-200 ${
                                isAddedToCart 
                                  ? (isPromo ? 'bg-gradient-to-r from-rose-400 to-orange-400' : 'bg-emerald-600')
                                  : isPromo
                                    ? 'bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 shadow-md hover:shadow-lg transform hover:scale-105'
                                    : 'bg-emerald-500 hover:bg-emerald-600 shadow-md hover:shadow-lg'
                              }`}
                            >
                              {isAddedToCart ? '✓ Selected' : (isPromo ? '🎉 Get Promo' : 'Select')}
                            </button>
                            
                            <button
                              onClick={handleAddToCart}
                              disabled={isAddedToCart}
                              className={`w-full py-1.5 px-3 rounded-lg font-medium text-xs transition-colors flex items-center justify-center gap-1 ${
                                isAddedToCart 
                                  ? 'opacity-50 cursor-not-allowed' 
                                  : isPromo
                                    ? 'bg-white text-rose-600 border-2 border-rose-300 hover:bg-rose-50'
                                    : 'text-gray-600 border border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              {isAddedToCart ? 'Added' : (isPromo ? '🛒 Add Promo to Cart' : 'Add to Cart')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
                })()}
              </>
            ) : (
              <div className="py-4 text-center text-gray-500 border-t mt-4">
                No rates available for the selected dates. Please try different dates.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Dynamically import the component that uses useSearchParams
// This ensures it's only loaded on the client side
const HomeContent = dynamic(() => import('@/components/HomeContent'), {
  ssr: false,
  loading: () => <LoadingUI />
});

// Client component to handle useSearchParams - ensure it's loaded client-side only
const SearchParamsWrapper = dynamic(() => import('@/components/SearchParamsWrapper'), {
  ssr: false,
  loading: () => <LoadingUI />
});

// Main component with Suspense boundary
export default function HomePage() {
  const [bannerImages, setBannerImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [showNoProperty, setShowNoProperty] = useState(false);
  const [propertyCode, setPropertyCode] = useState(null);
  const [showMemberLoginModal, setShowMemberLoginModal] = useState(false);
  const [showMembershipButton, setShowMembershipButton] = useState(false); // Start with false, will be set after fetch
  const [useMembership, setUseMembership] = useState(null);
  const [loggedInMember, setLoggedInMember] = useState(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [welcomeGuestName, setWelcomeGuestName] = useState('');
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  // Add URL parameter tracking to trigger refetch when dates change
  const [urlParameters, setUrlParameters] = useState("");
  const router = useRouter();
  
  // Check for existing member login session and initialize session monitoring
  useEffect(() => {
    const checkExistingLogin = async () => {
      try {
        // Check if session has expired first
        if (isSessionExpired()) {
          console.log('Session expired on page load, redirecting...');
          handleSessionExpiry();
          return;
        }

        // Check for invalid or missing property in URL and fix it immediately
        if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          const urlProperty = urlParams.get('property');
          
          console.log('🔍 Checking URL property:', urlProperty);
          
          // PRIORITY 1: If URL has a valid property, save it IMMEDIATELY
          if (urlProperty && urlProperty !== 'null' && urlProperty !== 'undefined' && urlProperty.trim() !== '') {
            // Save the URL property to localStorage FIRST
            const saved = savePropertyCode(urlProperty);
            console.log('✅ Property code from URL saved to localStorage:', urlProperty, '- Success:', saved);
            
            // Verify it was saved
            const verified = getStoredPropertyCode();
            console.log('✅ Verification - Property in localStorage:', verified);
            
            // Continue with normal flow - don't return here
          } else {
            // PRIORITY 2: No valid property in URL, try to restore from localStorage
            console.warn('⚠ Invalid or missing property detected in URL');
            const savedProperty = getStoredPropertyCode();
            
            // Use saved property or fallback to PMH
            const propertyToUse = savedProperty || 'PMH';
            urlParams.set('property', propertyToUse);
            
            // Ensure we have default dates
            if (!urlParams.get('checkIn')) {
              const today = new Date();
              const tomorrow = new Date(today);
              tomorrow.setDate(today.getDate() + 1);
              
              const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              
              urlParams.set('checkIn', formatDate(today));
              urlParams.set('checkOut', formatDate(tomorrow));
              urlParams.set('adults', '2');
              urlParams.set('children', '0');
              urlParams.set('room', '1');
            }
            
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            
            if (savedProperty) {
              console.log('✓ Restored property from localStorage:', savedProperty);
            } else {
              console.log('⚠ No saved property, using PMH fallback');
            }
            
            router.replace(newUrl);
            console.log('✓ URL fixed with property:', propertyToUse);
            return; // Exit and let the page reload with correct URL
          }
        }

        // Get property code
        const propertyCode = getPropertyCode() || 'default';
        console.log('✓ Final property code being used:', propertyCode);
        
        // Try to get session from localStorage directly
        const sessionKey = `memberLogin_${propertyCode}`;
        const sessionData = localStorage.getItem(sessionKey);
        
        if (sessionData) {
          const memberData = JSON.parse(sessionData);
          console.log('Found existing member login:', memberData);
          setLoggedInMember(memberData.memberData);
        }
      } catch (error) {
        console.error('Error checking existing login:', error);
      }
    };
    
    checkExistingLogin();

    // Check for returning guest
    if (typeof window !== 'undefined') {
      const returningGuestName = localStorage.getItem('returningGuestName');
      if (returningGuestName) {
        // Show a simple welcome toast/dialog
        // We'll use a custom event or a simple alert for now, or better yet, a state-based dialog
        setWelcomeGuestName(returningGuestName);
        setShowWelcomeDialog(true);
        // Clear it so it doesn't show every time? 
        // Maybe keep it for a session? Let's keep it for now.
        // Or maybe clear it after showing once per session?
        // Let's rely on the user closing it.
      }
    }

    // Initialize session monitoring
    const cleanupSessionMonitoring = initializeSessionMonitoring(() => {
      console.log('Session expired, handling redirect with saved property...');
      handleSessionExpiry();
    });

    // Update activity on component mount
    updateLastActivity();

    // Cleanup function
    return () => {
      if (cleanupSessionMonitoring) {
        cleanupSessionMonitoring();
      }
    };
  }, []);

  // Set document title and fetch hotel details
  // Effect to track URL parameters changes - runs only once on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Store the last checked parameters to avoid unnecessary updates
      let lastCheckedParams = window.location.search;
      
      // Function to check and update URL parameters
      const checkUrlParams = () => {
        const currentParams = window.location.search;
        if (currentParams !== lastCheckedParams) {
          console.log('URL parameters changed:', currentParams);
          lastCheckedParams = currentParams;
          setUrlParameters(currentParams);
        }
      };
      
      // Initial check
      checkUrlParams();
      
      // Set up event listener for URL changes
      window.addEventListener('popstate', checkUrlParams);
      
      // Set up event listener for history changes (catches pushState and replaceState)
      window.addEventListener('pushstate', checkUrlParams);
      window.addEventListener('replacestate', checkUrlParams);
      
      // Set interval to periodically check for URL changes (catches form submissions)
      const intervalId = setInterval(checkUrlParams, 1000);
      
      // Clean up event listeners and interval
      return () => {
        window.removeEventListener('popstate', checkUrlParams);
        window.removeEventListener('pushstate', checkUrlParams);
        window.removeEventListener('replacestate', checkUrlParams);
        clearInterval(intervalId);
      };
    }
  }, []);  // Empty dependency array - runs only once on mount

  // Effect to fetch hotel details when URL parameters change
  useEffect(() => {
    document.title = "Booking Engine";

    const initializeProperty = () => {
      const currentPropertyCode = ensureRequiredParams(router);
      
      // Don't validate format here - let API validation handle it
      // This allows any property code to be tried and saved
      if (!currentPropertyCode) {
        console.warn('No property code provided');
        setShowNoProperty(true);
        setIsLoading(false);
        return null;
      }
      
      // Save the property code immediately, regardless of format
      console.log('🔧 Initializing property:', currentPropertyCode);
      savePropertyCode(currentPropertyCode);
      setPropertyCode(currentPropertyCode);
      
      return currentPropertyCode;
    };
    
    // Fetch hotel banner images
    const fetchHotelBanners = async () => {
      try {
        // Extract current URL parameters
        const currentUrlParams = new URLSearchParams(urlParameters);
        const currentCheckIn = currentUrlParams.get('checkIn');
        const currentCheckOut = currentUrlParams.get('checkOut');
        const currentProperty = currentUrlParams.get('property');
        
        console.log(`Fetching hotel data for dates: ${currentCheckIn} to ${currentCheckOut}, property: ${currentProperty}`);
        
        // Start with loading state
        setIsLoading(true);
        
        // Don't set default banners - we'll only show banners if we get real data
        setBannerImages([]);
        
        // Initialize and validate property
        const validPropertyCode = initializeProperty();
        if (!validPropertyCode) {
          console.warn('Invalid property code, not fetching hotel details');
          return; // Property validation failed, showing no property page
        }

        // Always save property to both session storage and localStorage
        // This ensures property is persisted across sessions
        const urlParams = new URLSearchParams(window.location.search);
        const urlProperty = urlParams.get('property');
        
        console.log('📝 Saving property code:', validPropertyCode);
        
        // Save to session storage
        saveProperty(validPropertyCode, `Property ${validPropertyCode}`);
        
        // Also save to localStorage using propertyManager
        const savedToLocalStorage = savePropertyCode(validPropertyCode);
        
        console.log('✓ Property saved to storage:', validPropertyCode);
        console.log('  - Session storage: ✓');
        console.log('  - Local storage:', savedToLocalStorage ? '✓' : '✗');
        
        // Verify the save
        const verifyProperty = getStoredPropertyCode();
        console.log('✓ Verification - Property in localStorage:', verifyProperty);
        
        // Validate dates and redirect if necessary
        let shouldContinueFetching = true;
        if (typeof window !== 'undefined') {
          try {
            // Use the already extracted parameters instead of getting them again
            if (currentCheckIn) {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              
              // Safely parse the date with error handling
              let checkInDate;
              try {
                checkInDate = new Date(currentCheckIn);
                checkInDate.setHours(0, 0, 0, 0);
                
                // Check if date is valid
                if (isNaN(checkInDate.getTime())) {
                  throw new Error('Invalid date format');
                }
                
                if (checkInDate < today) {
                  console.log('Check-in date is before today, redirecting to today');
                  
                  const formattedToday = formatDate(today);
                  const nightsParam = urlParams.get('nights');
                  const parsedNights = nightsParam ? parseInt(nightsParam, 10) : NaN;
                  const nights = (!isNaN(parsedNights) && parsedNights > 0) ? parsedNights : 1;
                  
                  const checkoutDate = new Date(today);
                  checkoutDate.setDate(today.getDate() + nights);
                  const formattedCheckout = formatDate(checkoutDate);
                  
                  urlParams.set('checkIn', formattedToday);
                  urlParams.set('checkOut', formattedCheckout);
                  
                  router.push(`${window.location.pathname}?${urlParams.toString()}`);
                  shouldContinueFetching = false;
                  return;
                }
              } catch (dateError) {
                console.error('Error parsing date:', dateError);
                // Continue with fetching even if date parsing fails
              }
            }
          } catch (urlError) {
            console.error('Error processing URL parameters:', urlError);
            // Continue with fetching even if URL processing fails
          }
        }
        
        if (!shouldContinueFetching) return;
        
        console.log('Fetching hotel details with property code:', validPropertyCode);
        
        // Track fetch attempts to prevent infinite retries
        let fetchAttempts = 0;
        const MAX_FETCH_ATTEMPTS = 3;
        let hotelDetailsResponse = null;
        let fetchSuccess = false;
        
        // Try fetching with retries
        while (fetchAttempts < MAX_FETCH_ATTEMPTS && !fetchSuccess) {
          try {
            fetchAttempts++;
            console.log(`Attempt ${fetchAttempts}/${MAX_FETCH_ATTEMPTS} to fetch hotel details`);
            
            // Check if we need to refetch based on property code change
            const lastFetchedProperty = sessionStorage.getItem('lastFetchedProperty');
            
            // Always fetch fresh data, no cache expiry check
            // This ensures data is always up-to-date
            if (lastFetchedProperty !== validPropertyCode || !lastFetchedProperty) {
              console.log('Fetching fresh hotel details for property:', validPropertyCode);
              // The fetchHotelDetails function now has its own retry mechanism
              hotelDetailsResponse = await fetchHotelDetails(validPropertyCode);
              
              // Store the fetch information (without timestamp for expiry)
              sessionStorage.setItem('lastFetchedProperty', validPropertyCode);
              sessionStorage.setItem('cachedHotelDetails', JSON.stringify(hotelDetailsResponse));
              
              fetchSuccess = true;
            } else {
              console.log('Property unchanged, fetching fresh data anyway');
              // Always fetch fresh data to ensure it's up-to-date
              hotelDetailsResponse = await fetchHotelDetails(validPropertyCode);
              fetchSuccess = true;
            }
          } catch (fetchError) {
            console.error(`Error fetching hotel details (attempt ${fetchAttempts}/${MAX_FETCH_ATTEMPTS}):`, fetchError);
            
            // If we've reached max attempts, use default values
            if (fetchAttempts >= MAX_FETCH_ATTEMPTS) {
              console.warn('Max fetch attempts reached, using default values');
              break;
            }
            
            // Add exponential backoff delay before retrying
            const backoffDelay = Math.min(1000 * Math.pow(2, fetchAttempts), 5000);
            console.log(`Retrying in ${backoffDelay}ms...`);
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
          }
        }
        
        // If all fetch attempts failed, use default values
        if (!fetchSuccess) {
          console.warn('All fetch attempts failed, using default values');
          // Return null for hotel info when fetch fails
          setHotelInfo(null);
          // Don't set any default values for hotel name or address
          
          // Set CSS variables with default values
          if (typeof document !== 'undefined') {
            document.documentElement.style.setProperty('--primary-color', "#820c04");
            document.documentElement.style.setProperty('--secondary-color', "#383737");
            document.documentElement.style.setProperty('--tertiary-color', "#f5f2f2");
          }
          
          // We already set default banners, so just finish loading
          setIsLoading(false);
          return;
        }
        
        // Extract hotel information from response
        let hotelDetails = hotelDetailsResponse;
        
        // Check if the data is in a nested structure
        if (hotelDetailsResponse && hotelDetailsResponse.data) {
          hotelDetails = hotelDetailsResponse.data;
        }
        
        // If fetch returned null, fall back to default values
        if (!hotelDetails) {
          console.warn('Hotel details response is null, using default values');
          if (typeof document !== 'undefined') {
            document.documentElement.style.setProperty('--primary-color', "#820c04");
            document.documentElement.style.setProperty('--secondary-color', "#383737");
            document.documentElement.style.setProperty('--tertiary-color', "#f5f2f2");
          }
          setIsLoading(false);
          return;
        }
        
        console.log('Hotel details:', hotelDetails);
        
        // Check if use_membership flag is available and set visibility of membership button
        try {
          if (hotelDetails.use_membership !== undefined) {
            // Convert to number to ensure proper comparison
            const membershipValue = Number(hotelDetails.use_membership);
            console.log('🔍 page.js - Raw use_membership from API:', hotelDetails.use_membership, 'Type:', typeof hotelDetails.use_membership);
            console.log('🔍 page.js - Converted use_membership:', membershipValue, 'Type:', typeof membershipValue);
            console.log('🔍 page.js - Is use_membership === 3?', membershipValue === 3);
            
            // Store the use_membership value as number
            setUseMembership(membershipValue);
            
            // Set visibility of MemberLoginModal based on use_membership flag
            // Mode 0: No modal (false)
            // Mode 1: Show modal (true) - for member login
            // Mode 2: Show modal (true) - reserved
            // Mode 3: No modal (false) - uses Guest Portal button instead
            const shouldShowMemberLoginModal = membershipValue !== 0 && membershipValue !== 3;
            setShowMembershipButton(shouldShowMemberLoginModal);
            
            console.log('✅ page.js - MemberLoginModal visibility:', shouldShowMemberLoginModal);
            console.log('✅ page.js - use_membership value set to:', membershipValue);
            console.log('✅ page.js - Mode explanation:');
            if (membershipValue === 0) {
              console.log('   → Mode 0: No membership features');
            } else if (membershipValue === 1) {
              console.log('   → Mode 1: MemberLoginModal enabled');
            } else if (membershipValue === 2) {
              console.log('   → Mode 2: MemberLoginModal enabled (reserved)');
            } else if (membershipValue === 3) {
              console.log('   → Mode 3: Guest Portal button (no MemberLoginModal)');
            }
          } else {
            console.warn('⚠️ use_membership is undefined in hotel details');
          }
        } catch (err) {
          // Silently handle error without showing error overlay
          console.error('❌ Error processing use_membership flag:', err);
          // Default to showing the button in case of error
          setShowMembershipButton(true);
        }
        
        // Extract banner image URLs from hotel details
        const banners = [
          hotelDetails.url_banner,
          hotelDetails.url_banner2,
          hotelDetails.url_banner3,
          hotelDetails.url_banner4,
          hotelDetails.url_banner5,
          hotelDetails.url_banner6,
          hotelDetails.url_banner7,
          hotelDetails.url_banner8
        ].filter(url => url !== null && url !== undefined && url !== "");
        
        console.log("Hotel banners fetched:", banners);
        
        // Log banner info for debugging
        console.log("Banner Info 1:", hotelDetails.banner_info1);
        console.log("Banner Info 2:", hotelDetails.banner_info2);
        
        // Set hotel info with proper structure
        setHotelInfo({
          NAME: hotelDetails.NAME || "Hotel Name",
          address: hotelDetails.address || "JL.Gandekan LOR 47, Malioboro Yogyakarta",
          primarycolor: hotelDetails.primarycolor || "#820c04",
          secondarycolor: hotelDetails.secondarycolor || "#383737",
          tertiary: hotelDetails.tertiary || "#f5f2f2",
          banner_info1: hotelDetails.banner_info1 || null,
          banner_info2: hotelDetails.banner_info2 || null,
          promotion_banner: hotelDetails.promotion_banner || null,
          promotion_banner2: hotelDetails.promotion_banner2 || null
        });
        
        console.log("Hotel info set with banner_info1:", hotelDetails.banner_info1, "banner_info2:", hotelDetails.banner_info2);
        
        // Set CSS variables for the theme colors
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--primary-color', hotelDetails.primarycolor || "#820c04");
          document.documentElement.style.setProperty('--secondary-color', hotelDetails.secondarycolor || "#383737");
          document.documentElement.style.setProperty('--tertiary-color', hotelDetails.tertiary || "#f5f2f2");
        }
        
        // Update banners only if we have actual banners from the API
        if (banners.length > 0) {
          console.log('Setting actual hotel banners:', banners);
          setBannerImages(banners);
        } else {
          console.warn('No banner images found in hotel details, keeping default banners');
          // Default banners were already set at the beginning
        }
      } catch (error) {
        // Silently handle error without showing error overlay
        console.error("Error fetching hotel banners:", error);
        
        // Check if it's a property not found error
        if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
          console.warn('⚠ Property not found in API, but keeping property code saved');
          
          // IMPORTANT: Don't show "No Property Available" immediately
          // The property code is already saved in localStorage
          // Just show a warning message instead
          console.log('💾 Property code remains saved in localStorage for future use');
          
          // Set a flag to show a different message
          setShowNoProperty(true);
          setIsLoading(false);
          return;
        }
        
        // Set hotel info to null if fetch fails
        setHotelInfo(null);
        
        // Set default CSS variables
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--primary-color', "#820c04");
          document.documentElement.style.setProperty('--secondary-color', "#383737");
          document.documentElement.style.setProperty('--tertiary-color', "#f5f2f2");
        }
        
        // Don't use default banners in case of error
        console.warn('Error fetching hotel data, not showing any banners');
        setBannerImages([]);
        setIsLoading(false);
      } finally {
        // Always set loading to false when done
        setIsLoading(false);
      }
    };
    
    fetchHotelBanners();
  }, [router, urlParameters]); // Add urlParameters to dependency array to trigger refetch when dates change
  
  // Auto-refresh data every 2 minutes to keep it up-to-date
  useEffect(() => {
    console.log('🔄 Setting up auto-refresh interval (every 2 minutes)');
    
    // Create interval to refresh data every 2 minutes (120000ms)
    const refreshInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing data...');
      
      // Trigger a re-fetch by updating urlParameters
      if (typeof window !== 'undefined') {
        const currentParams = window.location.search;
        setUrlParameters(currentParams + '&refresh=' + new Date().getTime());
      }
    }, 120000); // 2 minutes = 120000 milliseconds
    
    // Cleanup interval on component unmount
    return () => {
      console.log('🛑 Clearing auto-refresh interval');
      clearInterval(refreshInterval);
    };
  }, []); // Empty dependency array - runs once on mount
  
  // Show no property page if property validation failed
  if (showNoProperty) {
    return <NoPropertyAvailable />;
  }
  
  // Generate Schema.org JSON-LD for Hotel SEO
  const getHotelSchema = () => {
    if (!hotelInfo) return null;
    const baseUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/`
      : 'https://www.emerald-system.id/';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Hotel',
      name: hotelInfo.NAME || 'Hotel',
      url: baseUrl,
      telephone: hotelInfo.phone || hotelInfo.telephone || '',
      image: bannerImages.length > 0 ? bannerImages[0] : undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: hotelInfo.address || '',
        addressLocality: 'Yogyakarta',
        addressCountry: 'ID'
      },
      priceRange: '$$',
      starRating: {
        '@type': 'Rating',
        ratingValue: '4'
      },
      contactPoint: hotelInfo.whatsapp || hotelInfo.wa ? {
        '@type': 'ContactPoint',
        telephone: hotelInfo.whatsapp || hotelInfo.wa,
        contactType: 'reservations',
        availableLanguage: ['English', 'Indonesian']
      } : undefined
    };
    return JSON.stringify(schema);
  };

  return (
    <LanguageProvider>
      <CartProvider> {/* Wrap the app with CartProvider */}
        {/* Schema.org JSON-LD */}
        {hotelInfo && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: getHotelSchema() }}
          />
        )}
        {/* Session Warning Component */}
        <SessionWarning />
        <Suspense fallback={<LoadingUI />}>
          <div className="w-full mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-7xl">
            {/* Add Analytics component to track page views */}
            <Analytics />
            
            {/* Display banner carousel if images are available and hotelInfo exists */}
            {!isLoading && bannerImages.length > 0 && hotelInfo && (
              <HotelGallery bannerImages={bannerImages} hotelInfo={hotelInfo} />
            )}
            
            {/* Show a message if no images are available but we're still on this page */}
            {!isLoading && bannerImages.length === 0 && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Hotel information could not be loaded. Please check your connection or try again later.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Search Form */}
            <div className="relative z-10 px-2 sm:px-0">
              <SearchForm />
            </div>
            
            {/* Promo Banner Section - Shows active promotions */}
            <PromoBannerSection />
            
            {/* Render SearchParamsWrapper to handle useSearchParams with proper Suspense boundaries */}
            <div id="search-params-wrapper">
              <SearchParamsWrapper />
            </div>
          </div>
          
          {/* Show room skeleton while hotel info is loading */}
          {isLoading && !hotelInfo && (
            <div className="min-h-screen bg-gray-50">
              <div className="container mx-auto px-4 py-6">
                <div className="animate-pulse">
                  <div className="h-8 bg-gray-300 rounded w-48 mb-6"></div>
                  <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-100">
                    <div className="h-6 bg-gray-300 rounded w-48 mb-4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-50 p-3 rounded-md">
                        <div className="h-4 bg-gray-300 rounded w-20 mb-2"></div>
                        <div className="h-5 bg-gray-300 rounded w-32"></div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md">
                        <div className="h-4 bg-gray-300 rounded w-16 mb-2"></div>
                        <div className="h-5 bg-gray-300 rounded w-24"></div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md">
                        <div className="h-4 bg-gray-300 rounded w-12 mb-2"></div>
                        <div className="h-5 bg-gray-300 rounded w-16"></div>
                      </div>
                    </div>
                  </div>
                  <RoomSkeletonList count={3} />
                </div>
              </div>
            </div>
          )}

          {/* Load the HomeContent component which will display the room listings only if hotelInfo exists */}
          {hotelInfo && <HomeContent hotelInfo={hotelInfo} />}
        </Suspense>
        {/* Floating Cart component with profile options */}
        <ErrorBoundary>
          <FloatingCart loggedInMember={loggedInMember} setLoggedInMember={setLoggedInMember} />
        </ErrorBoundary>
        
        {/* Floating Membership Button - based on use_membership mode */}
        {/* IMPORTANT: Only render after API is loaded and useMembership is set */}
        {!isLoading && useMembership !== null && (() => {
          console.log('🔍 Checking floating membership button in page.js');
          console.log('   - isLoading:', isLoading);
          console.log('   - useMembership:', useMembership, 'Type:', typeof useMembership);
          console.log('   - loggedInMember:', !!loggedInMember);
          console.log('   - Comparison (useMembership === 3):', useMembership === 3);
          console.log('   - Comparison (useMembership == 3):', useMembership == 3);
          
          // Mode 0: No button at all
          if (useMembership === 0) {
            console.log('ℹ️ Mode 0: No floating membership button');
            return null;
          }
          
          // Mode 1: FloatingLoginButton handles this (from ClientLayout.js)
          // No custom button needed in page.js for mode 1
          if (useMembership === 1) {
            console.log('ℹ️ Mode 1: FloatingLoginButton handled by ClientLayout.js');
            return null;
          }
          
          // Mode 2: Reserved for future use
          if (useMembership === 2) {
            console.log('ℹ️ Mode 2: Reserved for future use');
            return null;
          }
          
          // Mode 3: Guest Portal Button (always show, even if logged in)
          if (useMembership === 3) {
            console.log('✅✅✅ Mode 3 DETECTED: Rendering Guest Portal floating button');
            console.log('   - Button should appear at bottom-right corner');
            console.log('   - Link: https://guest.emerald-system.id');
            return (
              <a
                href="https://guest.emerald-system.id"
                target="_blank"
                rel="noopener noreferrer"
                className="group fixed bottom-20 right-6 z-50"
                title="Guest Portal"
                aria-label="Open Guest Portal"
              >
                <div className="relative">
                  {/* Pulse ring */}
                  <span className="absolute inset-0 rounded-full bg-blue-500/30 animate-ping pointer-events-none"></span>
                  {/* Hover label */}
                  <span className="absolute right-full top-1/2 -translate-y-1/2 mr-3 whitespace-nowrap rounded-lg bg-gray-900 text-white text-xs font-medium px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg border border-white/10">
                    Guest Portal
                  </span>
                  {/* Main button */}
                  <div className="relative flex items-center justify-center w-[60px] h-[60px] rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-2xl shadow-blue-900/30 ring-4 ring-white/40 group-hover:scale-110 group-hover:shadow-blue-500/40 group-active:scale-95 transition-all duration-300">
                    <svg className="w-7 h-7 drop-shadow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {/* Status dot */}
                    <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white"></span>
                  </div>
                </div>
              </a>
            );
          }
          
          // Default: No button for unknown modes
          console.log('⚠️ Unknown use_membership mode:', useMembership);
          return null;
        })()}
        
        {/* Member Login Modal - only rendered if membership is enabled and API is loaded */}
        {!isLoading && showMembershipButton && (
          <MemberLoginModal 
            isOpen={showMemberLoginModal} 
            onClose={() => setShowMemberLoginModal(false)}
            onLoginSuccess={(memberData) => {
              console.log('Member login successful:', memberData);
              // Set logged in member immediately to show welcome message
              setLoggedInMember(memberData);
              setShowMemberLoginModal(false);
              
              // Force re-render to ensure UI updates
              setTimeout(() => {
                console.log('Forcing re-render after login');
                setLoggedInMember({...memberData});
              }, 100);
            }}
          />
        )}
      {/* Welcome Back Dialog */}
      {showWelcomeDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="bg-gradient-to-r from-primary to-primary-dark p-6 text-white">
              <div className="flex justify-between items-start">
                <div className="bg-white/20 p-3 rounded-full mb-4 inline-block backdrop-blur-sm">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <button 
                  onClick={() => setShowWelcomeDialog(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <h3 className="text-2xl font-bold mb-1">Welcome Back!</h3>
              <p className="text-blue-100">It's great to see you again.</p>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 text-lg mb-6">
                Hello, <span className="font-bold text-gray-900">{welcomeGuestName}</span>! 👋
                <br />
                <span className="text-base text-gray-500 mt-2 block">Ready for your next stay?</span>
              </p>
              
              <button
                onClick={() => setShowWelcomeDialog(false)}
                className="w-full bg-primary hover:bg-primary-dark text-white font-medium py-3 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Continue Booking
              </button>
            </div>
          </div>
        </div>
      )}

      </CartProvider> {/* Close CartProvider */}
    </LanguageProvider>
  );
}
